import { useEffect, useState, type MouseEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './landing.css';
import './v2/v2-chrome.css';
import './v2/v2-site.css';
import { useAuth0 } from '@auth0/auth0-react';
import { useI18n } from '../../i18n/LanguageContext';
import { landingCopy } from './copy';
import LandingSeo from './LandingSeo';
import LanguageToggle from './LanguageToggle';
import {
    buildLandingPathForMarket,
    getFooterCopy,
    getRegionalCopyBlock,
    resolveLandingContext,
    type LandingPageKey,
} from './regional';
import {
    getLandingCatalogPage,
    getLandingCatalogPageByPath,
    getLandingTopNav,
    getLocalizedSolutionsBusinessSizeItems,
    getLocalizedSolutionsOtherItems,
    getLocalizedSolutionsRoleItems,
    getLocalizedSolutionsSpecialtyGroups,
} from './siteContent';

const CONTACT_EMAIL = 'info@elenethor.com';
const CONTACT_MAILTO = `mailto:${CONTACT_EMAIL}`;

export default function LandingLayout({ children, pageKey }: { children: React.ReactNode; pageKey: LandingPageKey }) {
    const [scrolled, setScrolled] = useState(false);
    const [navTucked, setNavTucked] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [navHoverDisabled, setNavHoverDisabled] = useState(false);
    const [solutionsMenuGroupIndex, setSolutionsMenuGroupIndex] = useState(1);
    const location = useLocation();
    const { loginWithRedirect, isAuthenticated } = useAuth0();
    const navigate = useNavigate();
    const { language, setLanguage } = useI18n();
    const { locale, market, htmlLang, basePath } = resolveLandingContext(location.pathname, language);
    const copy = landingCopy[locale];
    const regionalBlock = getRegionalCopyBlock(pageKey, market);
    const footer = getFooterCopy(market, locale);
    const toMarketPath = (path: string) => {
        return buildLandingPathForMarket(path, market);
    };
    const footerLanguageHeading = locale === 'es' ? 'Idioma' : 'Language';
    const currentCatalogPageKey = getLandingCatalogPageByPath(basePath);
    const spanishPageUrl = buildLandingPathForMarket(basePath, 'es');
    const visibleTopNav = getLandingTopNav(locale).filter((item) => item.key !== 'resources');
    const solutionsSpecialtyGroups = getLocalizedSolutionsSpecialtyGroups(locale);
    const solutionsRoleItems = getLocalizedSolutionsRoleItems(locale);
    const solutionsBusinessSizeItems = getLocalizedSolutionsBusinessSizeItems(locale);
    const solutionsOtherItems = getLocalizedSolutionsOtherItems(locale);
    useEffect(() => {
        // Force light mode for landing pages
        document.documentElement.setAttribute('data-theme', 'light');

        const handleScroll = () => {
            if (window.scrollY > 20) {
                setScrolled(true);
            } else {
                setScrolled(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        let lastY = window.scrollY;
        const handleDirectionalScroll = () => {
            const y = window.scrollY;
            setNavTucked(y > 180 && y > lastY + 2);
            lastY = y;
        };
        window.addEventListener('scroll', handleDirectionalScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleDirectionalScroll);
    }, []);

    useEffect(() => {
        if (market !== 'default' && language !== 'es') {
            setLanguage('es');
        }
    }, [language, market, setLanguage]);

    useEffect(() => {
        if (market !== 'default') return;
        const params = new URLSearchParams(location.search);
        const queryLang = params.get('lang');
        if (queryLang === 'en' || queryLang === 'es') {
            if (queryLang !== language) setLanguage(queryLang);
        }
    }, [language, location.search, market, setLanguage]);

    useEffect(() => {
        setMobileMenuOpen(false);
        setNavHoverDisabled(false);
        setSolutionsMenuGroupIndex(1);
        window.scrollTo(0, 0);
    }, [location.pathname, location.search]);

    const isActive = (path: string) => basePath === path ? 'active' : '';
    const isTopNavActive = (path: string) => {
        if (basePath === path) return 'active';
        if (!currentCatalogPageKey) return '';
        const currentCatalogPage = getLandingCatalogPage(currentCatalogPageKey, locale);
        if (path === '/features' && ['features', 'emr-ehr', 'clinical-ai', 'patient-portal', 'calendar-booking', 'billing-payments', 'multi-site-operations', 'reports-insights', 'marketing-growth'].includes(currentCatalogPageKey)) return 'active';
        if (path === '/who-we-serve' && currentCatalogPage.navGroup === 'who-we-serve') return 'active';
        if (path === '/why-elen1' && currentCatalogPageKey === 'why-elen1') return 'active';
        if (path === '/resources' && ['resources', 'help-center', 'developer-api', 'guides'].includes(currentCatalogPageKey)) return 'active';
        if (path === '/pricing' && currentCatalogPageKey === 'pricing') return 'active';
        return '';
    };
    const topNavPathByKey = {
        features: '/features',
        'who-we-serve': '/who-we-serve',
        'why-elen1': '/why-elen1',
        resources: '/resources',
        pricing: '/pricing',
    } as const;
    const toLanguageUrl = (target: 'en' | 'es') => {
        if (target === 'es') {
            return buildLandingPathForMarket(basePath, 'es');
        }
        const params = new URLSearchParams(location.search);
        params.delete('lang');
        const query = params.toString();
        return query ? `${basePath}?${query}` : basePath;
    };

    const closeNavigationChrome = () => {
        setMobileMenuOpen(false);
        setNavHoverDisabled(true);
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
    };

    const navigateWithLandingTransition = (path: string) => {
        const currentPath = `${location.pathname}${location.search}`;
        if (currentPath === path) {
            closeNavigationChrome();
            window.scrollTo({ top: 0, behavior: 'smooth' });
            requestAnimationFrame(() => setNavHoverDisabled(false));
            return;
        }

        const enhancedDocument = document as Document & {
            startViewTransition?: (update: () => void) => void;
        };
        const completeNavigation = () => navigate(path);

        closeNavigationChrome();
        if (typeof enhancedDocument.startViewTransition === 'function') {
            enhancedDocument.startViewTransition(() => {
                completeNavigation();
            });
            return;
        }

        completeNavigation();
    };

    const handleLandingLinkClick = (event: MouseEvent<HTMLAnchorElement>, path: string) => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.altKey || event.ctrlKey || event.shiftKey) {
            return;
        }
        event.preventDefault();
        navigateWithLandingTransition(path);
    };

    return (
        <div className={`landing-root landing-chrome-v2 ${navHoverDisabled ? 'nav-hover-disabled' : ''}`}>
            <LandingSeo pageKey={pageKey} htmlLang={htmlLang} locale={locale} />
            <nav className={`navbar ${scrolled ? 'scrolled' : ''} ${navTucked && !mobileMenuOpen ? 'nav-tucked' : ''}`}>
                <Link to={toMarketPath('/')} className="nav-logo" onClick={(event) => handleLandingLinkClick(event, toMarketPath('/'))}>
                    elen<span>1</span>
                </Link>
                <div className="nav-links">
                    {visibleTopNav.map((item) => (
                        item.groups ? (
                            <div key={item.key} className={`nav-dropdown nav-mega-dropdown ${isTopNavActive(topNavPathByKey[item.key])}`}>
                                <Link
                                    to={toMarketPath(topNavPathByKey[item.key])}
                                    className={isTopNavActive(topNavPathByKey[item.key])}
                                    onClick={(event) => handleLandingLinkClick(event, toMarketPath(topNavPathByKey[item.key]))}
                                >
                                    {item.label}
                                    <i className="ph ph-caret-down" aria-hidden="true"></i>
                                </Link>
                                <div className={`nav-dropdown-menu nav-mega-menu nav-mega-${item.key} cols-${item.groups.length} ${item.aside ? 'has-aside' : ''}`}>
                                    {item.key === 'features' ? (
                                        <>
                                            <div className="features-menu-main">
                                                <Link
                                                    to={toMarketPath('/features')}
                                                    className="features-menu-banner"
                                                    onClick={(event) => handleLandingLinkClick(event, toMarketPath('/features'))}
                                                >
                                                    <i className="ph ph-star" aria-hidden="true"></i>
                                                    <span>{locale === 'es' ? 'Explora los flujos clínicos más usados' : 'Explore our most-used clinic workflows'}</span>
                                                </Link>
                                                <div className="features-menu-columns">
                                                    <div className="features-menu-column">
                                                        <p className="nav-mega-heading">{locale === 'es' ? 'Funciones principales' : 'Core features'}</p>
                                                        {[...item.groups[0].items, ...item.groups[1].items].map((entry) => {
                                                            const page = getLandingCatalogPage(entry.key, locale);
                                                            return (
                                                                <Link
                                                                    key={entry.key}
                                                                    to={toMarketPath(page.path)}
                                                                    className={`nav-mega-item ${isActive(page.path)}`}
                                                                    onClick={(event) => handleLandingLinkClick(event, toMarketPath(page.path))}
                                                                >
                                                                    <span className="nav-mega-icon">
                                                                        <i className={`ph ${entry.icon}`} aria-hidden="true"></i>
                                                                    </span>
                                                                    <span>
                                                                        <strong>{entry.label}</strong>
                                                                        <small>{entry.description}</small>
                                                                    </span>
                                                                </Link>
                                                            );
                                                        })}
                                                    </div>
                                                    <div className="features-menu-column">
                                                        <p className="nav-mega-heading">{locale === 'es' ? 'Más consultadas' : 'Top picks'}</p>
                                                        {[...item.groups[2].items, ...item.groups[3].items].map((entry) => {
                                                            const page = getLandingCatalogPage(entry.key, locale);
                                                            return (
                                                                <Link
                                                                    key={entry.key}
                                                                    to={toMarketPath(page.path)}
                                                                    className={`nav-mega-item ${isActive(page.path)}`}
                                                                    onClick={(event) => handleLandingLinkClick(event, toMarketPath(page.path))}
                                                                >
                                                                    <span className="nav-mega-icon">
                                                                        <i className={`ph ${entry.icon}`} aria-hidden="true"></i>
                                                                    </span>
                                                                    <span>
                                                                        <strong>{entry.label}</strong>
                                                                        <small>{entry.description}</small>
                                                                    </span>
                                                                </Link>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                            {item.aside ? (
                                                <div className="nav-mega-aside features-menu-aside">
                                                    <div>
                                                        <p className="nav-mega-heading accent">{item.aside.eyebrow}</p>
                                                        <img src="/landing-captures/dashboard-standalone.png" alt="" aria-hidden="true" />
                                                        <h3>{item.aside.title}</h3>
                                                        <p>{item.aside.body}</p>
                                                    </div>
                                                    <Link
                                                        to={toMarketPath(getLandingCatalogPage(item.aside.ctaKey, locale).path)}
                                                        className="nav-mega-aside-link"
                                                        onClick={(event) => handleLandingLinkClick(event, toMarketPath(getLandingCatalogPage(item.aside.ctaKey, locale).path))}
                                                    >
                                                        <span>{item.aside.ctaLabel}</span>
                                                        <span className="nav-mega-aside-arrow" aria-hidden="true">
                                                            <i className="ph ph-arrow-up-right"></i>
                                                        </span>
                                                    </Link>
                                                </div>
                                            ) : null}
                                        </>
                                    ) : item.key === 'who-we-serve' ? (
                                        <>
                                            <div className="solutions-menu-rail">
                                                {solutionsSpecialtyGroups.map((group, index) => {
                                                    const categoryPage = getLandingCatalogPage(group.categoryKey, locale);
                                                    return (
                                                        <Link
                                                            key={group.title}
                                                            to={toMarketPath(categoryPage.path)}
                                                            className="solutions-menu-category"
                                                            onMouseEnter={() => setSolutionsMenuGroupIndex(index)}
                                                            onFocus={() => setSolutionsMenuGroupIndex(index)}
                                                            onClick={(event) => handleLandingLinkClick(event, toMarketPath(categoryPage.path))}
                                                        >
                                                            <span><i className={`ph ${group.icon}`} aria-hidden="true"></i></span>
                                                            <strong>{group.title}</strong>
                                                            <i className="ph ph-caret-right" aria-hidden="true"></i>
                                                        </Link>
                                                    );
                                                })}
                                            </div>
                                            <div className="features-menu-main solutions-menu-main">
                                                <Link
                                                    to={toMarketPath('/who-we-serve')}
                                                    className="features-menu-banner solutions-menu-banner"
                                                    onClick={(event) => handleLandingLinkClick(event, toMarketPath('/who-we-serve'))}
                                                >
                                                    <i className={`ph ${solutionsSpecialtyGroups[solutionsMenuGroupIndex].icon}`} aria-hidden="true"></i>
                                                    <span>{solutionsSpecialtyGroups[solutionsMenuGroupIndex].banner}</span>
                                                </Link>
                                                <div className="solutions-specialty-grid">
                                                    {solutionsSpecialtyGroups[solutionsMenuGroupIndex].items.map((entry) => {
                                                        const page = getLandingCatalogPage(entry.key, locale);
                                                        return (
                                                            <Link
                                                                key={entry.key}
                                                                to={toMarketPath(page.path)}
                                                                className={`solutions-specialty-item ${isActive(page.path)}`}
                                                                onClick={(event) => handleLandingLinkClick(event, toMarketPath(page.path))}
                                                            >
                                                                <span className="nav-mega-icon">
                                                                    <i className={`ph ${entry.icon}`} aria-hidden="true"></i>
                                                                </span>
                                                                <span>
                                                                    <strong>{entry.label}</strong>
                                                                    <small>{entry.description}</small>
                                                                </span>
                                                            </Link>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                            <div className="solutions-menu-side">
                                                <div>
                                                    <p className="nav-mega-heading">{locale === 'es' ? 'Rol' : 'Role'}</p>
                                                    <div className="solutions-side-list">
                                                        {solutionsRoleItems.map((entry) => {
                                                            const page = getLandingCatalogPage(entry.key, locale);
                                                            return (
                                                                <Link
                                                                    key={entry.key}
                                                                    to={toMarketPath(page.path)}
                                                                    className={isActive(page.path)}
                                                                    onClick={(event) => handleLandingLinkClick(event, toMarketPath(page.path))}
                                                                >
                                                                    {entry.label}
                                                                </Link>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                                <div>
                                                    <p className="nav-mega-heading">{locale === 'es' ? 'Tamaño de negocio' : 'Business size'}</p>
                                                    <div className="solutions-side-list">
                                                        {solutionsBusinessSizeItems.map((entry) => {
                                                            const page = getLandingCatalogPage(entry.key, locale);
                                                            return (
                                                                <Link
                                                                    key={entry.key}
                                                                    to={toMarketPath(page.path)}
                                                                    className={isActive(page.path)}
                                                                    onClick={(event) => handleLandingLinkClick(event, toMarketPath(page.path))}
                                                                >
                                                                    {entry.label}
                                                                </Link>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                                <div>
                                                    <p className="nav-mega-heading">{locale === 'es' ? 'Otros' : 'Other'}</p>
                                                    <div className="solutions-side-list">
                                                        {solutionsOtherItems.map((entry) => {
                                                            const page = getLandingCatalogPage(entry.key, locale);
                                                            return (
                                                                <Link
                                                                    key={entry.key}
                                                                    to={toMarketPath(page.path)}
                                                                    className={isActive(page.path)}
                                                                    onClick={(event) => handleLandingLinkClick(event, toMarketPath(page.path))}
                                                                >
                                                                    {entry.label}
                                                                </Link>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            {item.groups.map((group) => (
                                                <div key={group.title} className="nav-mega-group">
                                                    <p className="nav-mega-heading">{group.title}</p>
                                                    <div className="nav-mega-list">
                                                        {group.items.map((entry) => {
                                                            const page = getLandingCatalogPage(entry.key, locale);
                                                            return (
                                                                <Link
                                                                    key={entry.key}
                                                                    to={toMarketPath(page.path)}
                                                                    className={`nav-mega-item ${isActive(page.path)}`}
                                                                    onClick={(event) => handleLandingLinkClick(event, toMarketPath(page.path))}
                                                                >
                                                                    <span className="nav-mega-icon">
                                                                        <i className={`ph ${entry.icon}`} aria-hidden="true"></i>
                                                                    </span>
                                                                    <span>
                                                                        <strong>{entry.label}</strong>
                                                                        <small>{entry.description}</small>
                                                                    </span>
                                                                </Link>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            ))}
                                            {item.aside ? (
                                                <div className="nav-mega-aside">
                                                    <div>
                                                        <p className="nav-mega-heading accent">{item.aside.eyebrow}</p>
                                                        <h3>{item.aside.title}</h3>
                                                        <p>{item.aside.body}</p>
                                                    </div>
                                                    <Link
                                                        to={toMarketPath(getLandingCatalogPage(item.aside.ctaKey, locale).path)}
                                                        className="nav-mega-aside-link"
                                                        onClick={(event) => handleLandingLinkClick(event, toMarketPath(getLandingCatalogPage(item.aside.ctaKey, locale).path))}
                                                    >
                                                        <span>{item.aside.ctaLabel}</span>
                                                        <span className="nav-mega-aside-arrow" aria-hidden="true">
                                                            <i className="ph ph-arrow-up-right"></i>
                                                        </span>
                                                    </Link>
                                                </div>
                                            ) : null}
                                        </>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <Link
                                key={item.key}
                                to={toMarketPath(topNavPathByKey[item.key])}
                                className={isTopNavActive(topNavPathByKey[item.key])}
                                onClick={(event) => handleLandingLinkClick(event, toMarketPath(topNavPathByKey[item.key]))}
                            >
                                {item.label}
                            </Link>
                        )
                    ))}
                </div>
                <div className="nav-right">
                    <LanguageToggle locale={locale} market={market} basePath={basePath} />
                    <div className="nav-cta-group">
                        <button
                            onClick={() => isAuthenticated ? navigate('/dashboard') : loginWithRedirect()}
                            className="btn-black cursor-pointer"
                        >
                            {isAuthenticated ? copy.layout.goToDashboard : copy.layout.loginRequestAccess}
                        </button>
                        <Link
                            to={toMarketPath('/book-demo')}
                            className="btn-lime btn-patient-portal"
                            onClick={(event) => handleLandingLinkClick(event, toMarketPath('/book-demo'))}
                        >
                            {locale === 'es' ? 'Reservar una demo' : 'Book a demo'}
                        </Link>
                    </div>
                    <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label={locale === 'es' ? 'Abrir menú móvil' : 'Toggle mobile menu'}>
                        <i className={`ph ${mobileMenuOpen ? 'ph-x' : 'ph-list'}`}></i>
                    </button>
                </div>
            </nav>

            {mobileMenuOpen && (
                <div className="mobile-nav-overlay" onClick={() => setMobileMenuOpen(false)}>
                    <div className="mobile-nav-menu" onClick={e => e.stopPropagation()}>
                        {visibleTopNav.map((item) => (
                            <div key={item.key} className="mobile-nav-group">
                                <Link
                                    to={toMarketPath(topNavPathByKey[item.key])}
                                    className={isTopNavActive(topNavPathByKey[item.key])}
                                    onClick={(event) => handleLandingLinkClick(event, toMarketPath(topNavPathByKey[item.key]))}
                                >
                                    {item.label}
                                </Link>
                                {item.groups ? (
                                    <div className="mobile-nav-submenu">
                                        {item.groups.map((group) => (
                                            <div key={group.title} className="mobile-nav-subgroup">
                                                <p>{group.title}</p>
                                                {group.items.map((entry) => {
                                                    const page = getLandingCatalogPage(entry.key, locale);
                                                    return (
                                                        <Link
                                                            key={entry.key}
                                                            to={toMarketPath(page.path)}
                                                            className={isActive(page.path)}
                                                            onClick={(event) => handleLandingLinkClick(event, toMarketPath(page.path))}
                                                        >
                                                            {entry.label}
                                                        </Link>
                                                    );
                                                })}
                                            </div>
                                        ))}
                                    </div>
                                ) : null}
                            </div>
                        ))}
                        <div className="mobile-lang-row">
                            <LanguageToggle locale={locale} market={market} basePath={basePath} />
                        </div>
                        <Link
                            to={toMarketPath('/book-demo')}
                            className="btn-lime mobile-demo-link"
                            onClick={(event) => handleLandingLinkClick(event, toMarketPath('/book-demo'))}
                        >
                            {locale === 'es' ? 'Reservar una demo' : 'Book a demo'}
                        </Link>
                    </div>
                </div>
            )}

            <div id="app">
                {children}

                {regionalBlock ? (
                    <section className="landing-seo-block">
                        <div className="container">
                            <h2>{regionalBlock.heading}</h2>
                            <p>{regionalBlock.body}</p>
                            <ul>
                                {regionalBlock.bullets.map((bullet) => (
                                    <li key={bullet}>{bullet}</li>
                                ))}
                            </ul>
                        </div>
                    </section>
                ) : null}

                <footer>
                    <div className="container landing-footer-top">
                        <div className="landing-footer-brand">
                            <h3>{footer.heading}</h3>
                            <p>{footer.body}</p>
                            <p className="landing-footer-compliance">{footer.compliance}</p>
                            <p className="footer-legal">{footer.legal}</p>
                        </div>
                        <div className="landing-footer-grid">
                            <div>
                                <h4>{locale === 'es' ? 'Producto' : 'Product'}</h4>
                                <div className="landing-footer-links">
                                    <Link to={toMarketPath('/features')}>{locale === 'es' ? 'Funciones' : 'Features'}</Link>
                                    <Link to={toMarketPath('/calendar-booking')}>{locale === 'es' ? 'Agenda' : 'Scheduling'}</Link>
                                    <Link to={toMarketPath('/billing-payments')}>{locale === 'es' ? 'Pagos' : 'Payments'}</Link>
                                    <Link to={toMarketPath('/reports-insights')}>{locale === 'es' ? 'Analítica' : 'Analytics'}</Link>
                                </div>
                            </div>
                            <div>
                                <h4>{locale === 'es' ? 'Casos de uso' : 'Use Cases'}</h4>
                                <div className="landing-footer-links">
                                    <Link to={toMarketPath('/who-we-serve')}>{locale === 'es' ? 'Soluciones' : "Who we're for"}</Link>
                                    <Link to={toMarketPath('/clinic-owners')}>{locale === 'es' ? 'Propietarios de clínica' : 'Clinic owners'}</Link>
                                    <Link to={toMarketPath('/practice-managers')}>{locale === 'es' ? 'Gerentes de práctica' : 'Practice managers'}</Link>
                                </div>
                            </div>
                            <div>
                                <h4>{locale === 'es' ? 'Descubrir' : 'Discover'}</h4>
                                <div className="landing-footer-links">
                                    <Link to={toMarketPath('/resources')}>{locale === 'es' ? 'Recursos' : 'Resources'}</Link>
                                    <Link to={toMarketPath('/pricing')}>{locale === 'es' ? 'Precios' : 'Pricing'}</Link>
                                </div>
                            </div>
                            <div>
                                <h4>{locale === 'es' ? 'Empresa' : 'Company'}</h4>
                                <div className="landing-footer-links">
                                    <Link to={toMarketPath('/why-elen1')}>{locale === 'es' ? 'Sobre nosotros' : 'About Us'}</Link>
                                    <Link to={toMarketPath('/security')}>{locale === 'es' ? 'Seguridad' : 'Security'}</Link>
                                    <a href={CONTACT_MAILTO}>{locale === 'es' ? 'Contacto' : 'Contact Us'}</a>
                                </div>
                            </div>
                            <div>
                                <h4>{footerLanguageHeading}</h4>
                                <div className="landing-footer-links">
                                    {/* navigation alone doesn't switch locale on the default market —
                                        the language state has to change with it */}
                                    <Link to={toLanguageUrl('en')} onClick={() => setLanguage('en')}>English</Link>
                                    <Link to={spanishPageUrl} onClick={() => setLanguage('es')}>Español</Link>
                                </div>
                            </div>
                            <div>
                                <h4>{locale === 'es' ? 'Siguiente paso' : 'Next step'}</h4>
                                <div className="landing-footer-links">
                                    <Link to={toMarketPath('/book-demo')}>{locale === 'es' ? 'Reservar una demo' : 'Book a demo'}</Link>
                                    <a href={CONTACT_MAILTO}>{locale === 'es' ? 'Contacto' : 'Contact Us'}</a>
                                </div>
                            </div>
                        </div>
                    </div>
                </footer>
            </div>
        </div>
    );
}
